import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  computed,
  inject,
  signal,
} from '@angular/core';
import { finalize } from 'rxjs';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';

import { Bestellung } from '../../models/bestellung.model';
import { BestellungStatus } from '../../models/bestellung-status.enum';
import { BestellungenService } from '../../services/bestellungen.service';
import { BestellungPdfService } from '../../services/bestellung-pdf.service';
import { AuthService } from '../../../../core/services/auth.service';

type StatusFilter = 'KRITISCH' | 'OFFEN' | BestellungStatus | 'ALLE';

type BestellungenSortKey =
  | 'filiale'
  | 'datum'
  | 'typ'
  | 'status'
  | 'restzeit'
  | 'zeitraum';

type SortDirection = 'asc' | 'desc';

@Component({
  selector: 'app-bestelluebersicht',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './bestelluebersicht.component.html',
  styleUrl: './bestelluebersicht.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BestelluebersichtComponent implements OnDestroy {
  private readonly bestellungenService = inject(BestellungenService);
  private readonly bestellungPdfService = inject(BestellungPdfService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly countdownTick = signal(Date.now());

  private countdownBaseline = Date.now();

  private countdownTimerId: ReturnType<typeof setInterval> | null = null;

  protected readonly BestellungStatus = BestellungStatus;

  protected readonly selectedFiliale = signal('ALLE');
  protected readonly selectedFilter = signal<StatusFilter>('OFFEN');
  protected readonly bestellungen = signal<Bestellung[]>([]);
  protected readonly isFilialeDropdownOpen = signal(false);
  protected readonly isLoading = signal(false);
  protected readonly skeletonRows = Array.from({ length: 5 });

  protected readonly sortKey =
    signal<BestellungenSortKey | null>(null);

  protected readonly sortDirection =
    signal<SortDirection>('asc');

  protected readonly filialen = computed(() => {
    const filialen = this.authService.getAllowedFilialenDetails();

    return [
      { value: 'ALLE', label: 'Alle Filialen' },
      ...filialen.map((filiale) => ({
        value:
          String(filiale.filialNummer),

        label:
          filiale.name
            ? `Filiale ${filiale.filialNummer} - ${filiale.name}`
            : `Filiale ${filiale.filialNummer}`,
      })),
    ];
  });

  protected readonly selectedFilialeLabel = computed(() => {
    return (
      this.filialen().find(
        (filiale) => filiale.value === this.selectedFiliale()
      )?.label ?? 'Alle Filialen'
    );
  });

  protected readonly filialeFilteredBestellungen =
    computed(() => {
      const selectedFiliale =
        this.selectedFiliale();

      if (selectedFiliale === 'ALLE') {
        return this.bestellungen();
      }

      return this.bestellungen().filter(
        (bestellung) =>
          String(
            bestellung.filialNummer
          ) === selectedFiliale
      );
    });

  protected readonly filteredBestellungen = computed(() => {
    const filtered = this.filialeFilteredBestellungen().filter((bestellung) =>
      this.matchesFilter(bestellung, this.selectedFilter())
    );

    return this.sortBestellungen(filtered);
  });

  protected readonly kritischCount = computed(() =>
    this.bestellungen().filter((bestellung) =>
      this.isBestellungKritisch(bestellung)
    ).length
  );

  protected readonly unbearbeitetCount = computed(() =>
    this.bestellungen().filter(
      (bestellung) =>
        bestellung.status === BestellungStatus.Unbearbeitet
    ).length
  );

  protected readonly inBearbeitungCount = computed(() =>
    this.bestellungen().filter(
      (bestellung) =>
        bestellung.status === BestellungStatus.InBearbeitung
    ).length
  );

  protected readonly fertigCount = computed(() =>
    this.bestellungen().filter(
      (bestellung) =>
        bestellung.status === BestellungStatus.Fertig
    ).length
  );

  protected readonly abgeschlossenCount = computed(() =>
    this.bestellungen().filter(
      (bestellung) =>
        bestellung.status === BestellungStatus.Abgeschlossen
    ).length
  );

  protected readonly offenCount = computed(
    () =>
      this.unbearbeitetCount() +
      this.inBearbeitungCount()
  );

  protected readonly eilbestellungenCount = computed(() =>
    this.bestellungen().filter(
      (bestellung) =>
        bestellung.typ === 'Eilbestellung'
    ).length
  );

  protected readonly globalTotalCount = computed(
    () => this.bestellungen().length
  );

  /**
   * Footer counts remain scoped to the selected Filiale.
   */
  protected readonly totalCount = computed(
    () => this.filialeFilteredBestellungen().length
  );

  protected readonly erledigtCount = computed(() =>
    this.filialeFilteredBestellungen().filter(
      (bestellung) =>
        bestellung.status === BestellungStatus.Fertig ||
        bestellung.status === BestellungStatus.Abgeschlossen
    ).length
  );

  protected readonly erledigtPercent = computed(() => {
    const total = this.totalCount();

    if (!total) {
      return 0;
    }

    return Math.round(
      (this.erledigtCount() / total) * 100
    );
  });

  protected readonly filialeKritischCount = computed(() =>
    this.filialeFilteredBestellungen().filter((bestellung) =>
      this.isBestellungKritisch(bestellung)
    ).length
  );

  protected readonly filialeUnbearbeitetCount = computed(() =>
    this.filialeFilteredBestellungen().filter(
      (bestellung) =>
        bestellung.status === BestellungStatus.Unbearbeitet
    ).length
  );

  protected readonly filialeInBearbeitungCount = computed(() =>
    this.filialeFilteredBestellungen().filter(
      (bestellung) =>
        bestellung.status === BestellungStatus.InBearbeitung
    ).length
  );

  protected readonly filialeFertigCount = computed(() =>
    this.filialeFilteredBestellungen().filter(
      (bestellung) =>
        bestellung.status === BestellungStatus.Fertig
    ).length
  );

  protected readonly filialeAbgeschlossenCount = computed(() =>
    this.filialeFilteredBestellungen().filter(
      (bestellung) =>
        bestellung.status === BestellungStatus.Abgeschlossen
    ).length
  );

  protected readonly filialeOffenCount = computed(
    () =>
      this.filialeUnbearbeitetCount() +
      this.filialeInBearbeitungCount()
  );

  protected readonly statusFilters = computed(() => [
    {
      label: 'Kritisch',
      value: 'KRITISCH' as const,
      count: this.filialeKritischCount(),
    },
    {
      label: 'Offen',
      value: 'OFFEN' as const,
      count: this.filialeOffenCount(),
    },
    {
      label: 'Unbearbeitet',
      value: BestellungStatus.Unbearbeitet,
      count: this.filialeUnbearbeitetCount(),
    },
    {
      label: 'In Bearbeitung',
      value: BestellungStatus.InBearbeitung,
      count: this.filialeInBearbeitungCount(),
    },
    {
      label: 'Fertig',
      value: BestellungStatus.Fertig,
      count: this.filialeFertigCount(),
    },
    {
      label: 'Abgeschlossen',
      value: BestellungStatus.Abgeschlossen,
      count: this.filialeAbgeschlossenCount(),
    },
    {
      label: 'Alle',
      value: 'ALLE' as const,
      count: this.totalCount(),
    },
  ]);

  constructor() {
    this.startCountdownTimer();

    this.isLoading.set(true);

    this.bestellungenService
      .getBestellungen()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (bestellungen) => {
          const now = Date.now();

          this.countdownBaseline = now;
          this.countdownTick.set(now);

          this.bestellungen.set(bestellungen);

          this.refreshOffeneBestellungenAnzahlIfNeeded(
            bestellungen
          );
        },
        error: () => {
          this.bestellungen.set([]);
        },
      });
  }

  ngOnDestroy(): void {
    if (this.countdownTimerId !== null) {
      clearInterval(this.countdownTimerId);
      this.countdownTimerId = null;
    }
  }

  @HostListener('document:click', ['$event'])
  protected closeFilialeDropdownOnOutsideClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isFilialeDropdownOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  protected closeFilialeDropdownOnEscape(): void {
    this.isFilialeDropdownOpen.set(false);
  }

  protected toggleFilialeDropdown(): void {
    this.isFilialeDropdownOpen.update((isOpen) => !isOpen);
  }

  protected selectFiliale(filialeValue: string): void {
    this.selectedFiliale.set(filialeValue);
    this.isFilialeDropdownOpen.set(false);
  }

  protected setFilter(filter: StatusFilter): void {
    this.selectedFilter.set(filter);
  }

  protected openBestellung(
    bestellung: Bestellung
  ): void {
    this.bestellungenService
      .rememberBestellungStatusBeforeDetail(
        bestellung.id,
        bestellung.status
      );

    this.router.navigate([
      '/bestellungen',
      bestellung.id,
    ]);
  }

  protected openBestellungPdf(
    bestellung: Bestellung,
    event: Event
  ): void {
    event.stopPropagation();

    this.bestellungPdfService
      .openBestelluebersicht(
        bestellung.id
      );
  }

  protected getStatusLabel(status: BestellungStatus): string {
    switch (status) {
      case BestellungStatus.Kritisch:
        return 'Kritisch';

      case BestellungStatus.Unbearbeitet:
        return 'Unbearbeitet';

      case BestellungStatus.InBearbeitung:
        return 'In Bearbeitung';

      case BestellungStatus.Fertig:
        return 'Fertig';

      case BestellungStatus.Abgeschlossen:
        return 'Abgeschlossen';

      default:
        return 'Unbekannt';
    }
  }

  protected getActionLabel(bestellung: Bestellung): string {
    if (bestellung.status === BestellungStatus.Abgeschlossen) {
      return 'Ansehen';
    }

    if (bestellung.status === BestellungStatus.InBearbeitung) {
      return 'Öffnen';
    }

    return 'Bearbeiten';
  }

  protected isSummaryActive(filter: StatusFilter): boolean {
    return this.selectedFilter() === filter;
  }

  private matchesFilter(
    bestellung: Bestellung,
    filter: StatusFilter
  ): boolean {
    if (filter === 'ALLE') {
      return true;
    }

    if (filter === 'KRITISCH') {
      return this.isBestellungKritisch(bestellung);
    }

    if (filter === 'OFFEN') {
      return (
        bestellung.status === BestellungStatus.Unbearbeitet ||
        bestellung.status === BestellungStatus.InBearbeitung
      );
    }

    return bestellung.status === filter;
  }

  protected getLiveRestzeit(
    bestellung: Bestellung
  ): string {
    if (!bestellung.restzeit || bestellung.restzeit === '—') {
      return '—';
    }

    return this.formatRemainingMinutes(
      this.getRemainingMinutes(bestellung)
    );
  }

  protected isBestellungKritisch(
    bestellung: Bestellung
  ): boolean {
    if (
      bestellung.status === BestellungStatus.Fertig ||
      bestellung.status === BestellungStatus.Abgeschlossen
    ) {
      return false;
    }

    if (
      bestellung.kritisch ||
      bestellung.status === BestellungStatus.Kritisch
    ) {
      return true;
    }

    const remainingMinutes =
      this.getRemainingMinutes(bestellung);

    return remainingMinutes < 4 * 60;
  }

  private refreshOffeneBestellungenAnzahlIfNeeded(
    bestellungen: Bestellung[]
  ): void {
    const previousBestellung =
      this.bestellungenService
        .consumeBestellungStatusBeforeDetail();

    if (!previousBestellung) {
      return;
    }

    if (
      previousBestellung.status ===
      BestellungStatus.Fertig ||
      previousBestellung.status ===
      BestellungStatus.Abgeschlossen
    ) {
      return;
    }

    const updatedBestellung =
      bestellungen.find(
        (bestellung) =>
          bestellung.id ===
          previousBestellung.id
      );

    if (
      updatedBestellung?.status !==
      BestellungStatus.Fertig
    ) {
      return;
    }

    this.bestellungenService
      .getOffeneBestellungenAnzahl()
      .subscribe({
        error: () => {
          // Der vorhandene Badge-Wert bleibt erhalten.
        },
      });
  }

  private startCountdownTimer(): void {
    this.countdownTimerId = setInterval(() => {
      this.countdownTick.set(Date.now());
    }, 60_000);
  }

  private isCountdownActive(
    bestellung: Bestellung
  ): boolean {
    return (
      bestellung.status !== BestellungStatus.Fertig &&
      bestellung.status !== BestellungStatus.Abgeschlossen
    );
  }

  private getRemainingMinutes(
    bestellung: Bestellung
  ): number {
    const initialMinutes =
      this.parseRestzeit(bestellung.restzeit);

    const elapsedMinutes = Math.floor(
      (
        this.countdownTick() -
        this.countdownBaseline
      ) / 60_000
    );

    return Math.max(
      0,
      initialMinutes - elapsedMinutes
    );
  }

  private formatRemainingMinutes(
    totalMinutes: number
  ): string {
    const safeMinutes = Math.max(
      0,
      Math.floor(totalMinutes)
    );

    const days = Math.floor(
      safeMinutes / 1440
    );

    const remainingAfterDays =
      safeMinutes % 1440;

    const hours = Math.floor(
      remainingAfterDays / 60
    );

    const minutes =
      remainingAfterDays % 60;

    const formattedTime =
      `${String(hours).padStart(2, '0')}:` +
      `${String(minutes).padStart(2, '0')}`;

    if (days > 0) {
      return `${days}T ${formattedTime}`;
    }

    return formattedTime;
  }

  protected setSort(key: BestellungenSortKey): void {
    const currentKey = this.sortKey();

    // Klick auf eine neue Spalte:
    // Sortierung beginnt aufsteigend.
    if (currentKey !== key) {
      this.sortKey.set(key);
      this.sortDirection.set('asc');
      return;
    }

    // Zweiter Zustand: ASC -> DESC
    if (this.sortDirection() === 'asc') {
      this.sortDirection.set('desc');
      return;
    }

    // Dritter Zustand: DESC -> Backend-Reihenfolge
    this.sortKey.set(null);
    this.sortDirection.set('asc');
  }

  protected isSortActive(key: BestellungenSortKey): boolean {
    return this.sortKey() === key;
  }

  protected getSortIcon(key: BestellungenSortKey): string {
    if (!this.isSortActive(key)) {
      return 'unfold_more';
    }

    return this.sortDirection() === 'asc'
      ? 'keyboard_arrow_up'
      : 'keyboard_arrow_down';
  }

  private sortBestellungen(
    items: Bestellung[]
  ): Bestellung[] {
    const key = this.sortKey();

    // Solange der Benutzer keine Sortierung auswählt,
    // bleibt die Reihenfolge des Backends unverändert.
    if (key === null) {
      return [...items];
    }

    const direction = this.sortDirection();

    return [...items].sort((a, b) => {
      const result =
        this.compareBestellungValue(
          a,
          b,
          key
        );

      return direction === 'asc'
        ? result
        : -result;
    });
  }

  private compareBestellungValue(
    a: Bestellung,
    b: Bestellung,
    key: BestellungenSortKey
  ): number {
    switch (key) {
      case 'filiale':
        return this.compareText(
          `${a.filialeName} ${a.filialNummer}`,
          `${b.filialeName} ${b.filialNummer}`
        );
      case 'datum':
        return this.compareNumber(
          this.parseGermanDate(a.datum),
          this.parseGermanDate(b.datum)
        );

      case 'typ':
        return this.compareText(a.typ, b.typ);

      case 'status':
        return this.compareNumber(
          this.getStatusSortValue(a.status),
          this.getStatusSortValue(b.status)
        );

      case 'restzeit':
        return this.compareNumber(
          this.getRemainingMinutes(a),
          this.getRemainingMinutes(b)
        );

      case 'zeitraum':
        return this.compareText(a.zeitraum, b.zeitraum);

      default:
        return 0;
    }
  }

  private compareText(a: string, b: string): number {
    return a.localeCompare(b, 'de', {
      numeric: true,
      sensitivity: 'base',
    });
  }

  private compareNumber(a: number, b: number): number {
    return a - b;
  }

  private parseGermanDate(value: string): number {
    const match = value.match(
      /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/
    );

    if (!match) {
      return 0;
    }

    const [, day, month, year] = match;

    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day)
    ).getTime();
  }

  private parseRestzeit(value: string): number {
    const days = Number(
      value.match(/(\d+)\s*T/i)?.[1] ?? 0
    );

    const hours = Number(
      value.match(/(\d+)\s*h/i)?.[1] ?? 0
    );

    const minutes = Number(
      value.match(/(\d+)\s*min/i)?.[1] ?? 0
    );

    const timeMatch = value.match(/(\d{1,2}):(\d{2})/);

    if (timeMatch) {
      return (
        days * 1440 +
        Number(timeMatch[1]) * 60 +
        Number(timeMatch[2])
      );
    }

    return days * 1440 + hours * 60 + minutes;
  }

  private getStatusSortValue(
    status: BestellungStatus
  ): number {
    switch (status) {
      case BestellungStatus.Kritisch:
        return 0;

      case BestellungStatus.Unbearbeitet:
        return 1;

      case BestellungStatus.InBearbeitung:
        return 2;

      case BestellungStatus.Fertig:
        return 3;

      case BestellungStatus.Abgeschlossen:
        return 4;

      default:
        return 5;
    }
  }
}